import { getLightOnOffData } from 'api';
import React, { useEffect, useState } from 'react';
import Chart from 'react-apexcharts';
import { useSelector } from 'react-redux';
import dummyData from '../data.json';

const LightScheduleChart = () => {
  const [lightData, setLightData] = useState([]);
  const date = useSelector((state) => state.customization.currentDate);
  const selectedTime = useSelector((state) => state.customization.selectedTime);
  const [isLoading, setIsLoading] = useState(true);

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toISOString().split('T')[0];
  };

  const formattedDate = formatDate(date);
  const source = JSON.parse(localStorage.getItem('source'));

  // Convert time (HH:MM) to total minutes from midnight
  const timeToMinutes = (time) => {
    if (!time) return 0;
    const [hours, minutes] = time.split(':').map(Number);
    return hours * 60 + minutes;
  };

  // Convert minutes back to time format (HH:MM) - rounded to nearest minute
  const minutesToTime = (minutes) => {
    const roundedMinutes = Math.round(minutes); // Round to nearest minute
    const hours = Math.floor(roundedMinutes / 60);
    const mins = roundedMinutes % 60;
    return `${String(hours).padStart(2, '0')}:${String(mins).padStart(2, '0')}`;
  };

  // Convert date to DD-MM-YY format for comparison
  const getFormattedDisplayDate = (dateStr) => {
    if (!dateStr) return '';

    try {
      const date = new Date(dateStr);
      if (isNaN(date.getTime())) return '';

      const day = date.getDate();
      const month = date.getMonth() + 1;
      const year = date.getFullYear().toString().slice(-2);
      return `${day}-${month}-${year}`;
    } catch (e) {
      return '';
    }
  };

  // Function to calculate padded min and max times
  const calculatePaddedTimeRange = (timings) => {
    if (!timings || timings.length === 0) {
      return { min: 0, max: 1440 }; // Default to full day if no data
    }

    // Get all start and end times
    const allTimes = timings.flatMap(({ start, end }) => [timeToMinutes(start), timeToMinutes(end)]);

    const minTime = Math.min(...allTimes);
    const maxTime = Math.max(...allTimes);

    // Calculate padded times (1 hour before and after)
    const paddedMin = Math.max(0, minTime - 60);
    const paddedMax = Math.min(1440, maxTime + 60);

    return { min: paddedMin, max: paddedMax };
  };

  useEffect(() => {
    if (!formattedDate) return;

    const displayDate = getFormattedDisplayDate(formattedDate);

    if (displayDate === '29-4-25') {
      const data = dummyData.lightData || [];
      setLightData(data);
      setIsLoading(false);
      return;
    }

    const fetchData = async () => {
      try {
        setIsLoading(true);
        const startTime = new Date(selectedTime.start_time).toUTCString();
        const endTime = new Date(selectedTime.end_time).toUTCString();
        const requestData = {
          date: formattedDate,
          source_id: source,
          start_time: startTime,
          end_time: endTime
        };
        const response = await getLightOnOffData(requestData);

        if (response?.data?.data) {
          const apiData = response.data.data[0]?.lightData || [];
          setLightData(apiData);
        } else {
          setLightData([]);
        }
      } catch (error) {
        console.error('Error fetching light schedule data:', error);
        setLightData([]);
      } finally {
        setIsLoading(false);
      }
    };

    fetchData();
  }, [formattedDate, source, selectedTime?.start_time_string, selectedTime?.end_time_string]);

  // Prepare Dumbbell Chart data
  const series = lightData.map((zone, index) => ({
    name: zone.zone,
    data: zone.timings.map(({ start, end }) => ({
      x: zone.zone,
      y: [timeToMinutes(start), timeToMinutes(end)]
    })),
    color: ['#FFD700', '#FFA500', '#FFEC8B', '#FFCC00', '#FFFF00'][index % 5]
  }));

  // Calculate padded time range
  const allTimings = lightData.flatMap((zone) => zone.timings.map(({ start, end }) => ({ start, end })));

  const { min: paddedMin, max: paddedMax } = calculatePaddedTimeRange(allTimings);

  // Chart options
  const options = {
    chart: {
      type: 'rangeBar',
      height: 400,
      toolbar: { show: false }
    },
    plotOptions: {
      bar: {
        horizontal: true,
        rangeBarGroupRows: true,
        borderRadius: 7,
        barHeight: '60%'
      }
    },
    xaxis: {
      type: 'numeric',
      min: paddedMin,
      max: paddedMax,
      tickAmount: 8,
      labels: {
        formatter: (value) => {
          // Ensure we're working with whole minutes
          const roundedValue = Math.round(value);
          return minutesToTime(roundedValue);
        },
        style: {
          fontSize: '12px'
        }
      },
      title: {
        text: 'Time',
        style: {
          fontSize: '14px'
        }
      }
    },
    yaxis: {
      title: {
        text: 'Departments',
        style: {
          fontSize: '14px'
        }
      }
    },
    legend: {
      position: 'top',
      horizontalAlign: 'center',
      fontSize: '14px',
      markers: {
        width: 12,
        height: 12,
        radius: 6
      },
      itemMargin: {
        horizontal: 10,
        vertical: 5
      }
    },
    tooltip: {
      custom: function ({ seriesIndex, dataPointIndex, w }) {
        const data = w.config.series[seriesIndex].data[dataPointIndex];
        return `
          <div class="apexcharts-tooltip-title" style="font-weight: bold; text-align: center; margin-bottom: 5px;">
            ${data.x}
          </div>
          <div style="padding: 5px 10px; background: rgba(0,0,0,0.05); border-radius: 4px;">
            <span>${minutesToTime(Math.round(data.y[0]))} - ${minutesToTime(Math.round(data.y[1]))}</span>
          </div>
        `;
      },
      style: {
        fontSize: '14px'
      }
    },
    grid: {
      show: true,
      borderColor: '#f1f1f1',
      strokeDashArray: 0,
      position: 'back'
    },
    responsive: [
      {
        breakpoint: 768,
        options: {
          chart: {
            height: 500
          },
          xaxis: {
            tickAmount: 6,
            labels: {
              fontSize: '10px'
            }
          },
          yaxis: {
            labels: {
              fontSize: '10px'
            }
          }
        }
      }
    ]
  };

  if (isLoading) {
    return (
      <div
        className="chart-loading-container"
        style={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          height: '480px',
          flexDirection: 'column',
          backgroundColor: '#f8f9fa',
          border: '1px solid #e9ecef',
          borderRadius: '8px'
        }}
      >
        <div
          className="spinner"
          style={{
            width: '40px',
            height: '40px',
            border: '4px solid rgba(0, 0, 0, 0.1)',
            borderRadius: '50%',
            borderLeftColor: '#44BE54',
            animation: 'spin 1s linear infinite',
            marginBottom: '16px'
          }}
        ></div>
        <style>{`
          @keyframes spin {
            to {
              transform: rotate(360deg);
            }
          }
        `}</style>
        <div style={{ fontWeight: '500', color: '#555' }}>Loading Light Schedule Data...</div>
      </div>
    );
  }

  return (
    <div style={{ position: 'relative', width: '100%' }}>
      <Chart options={options} series={series} type="rangeBar" height={480} />
    </div>
  );
};

export default LightScheduleChart;
